import React, { useState, useEffect, useRef } from "react";
import { orderService } from "../services/dataService";
import { OrderImage } from "../types";
import { getImageUrl } from "../utils/formatters";
import { IMAGE_BASE_URL } from "../services/api";
import AlertModal from "./AlertModal";
import ConfirmModal from "./ConfirmModal";
import "../pages/PurchaseOrders.css";
import "./ImageManager.css";

/**
 * Sipariş resimleri — Siparişler, Fatura Yönetimi, Üzerimdeki İşler ve Mali Raporlar'dan açılır.
 * po-* penceresi + im-* ekleri (ImageManager.css): sürükle-bırak yükleme alanı, küçük resim ızgarası, koyu önizleme (lightbox).
 * Esc sırası: onay penceresi → önizleme → bu pencere; yükleme sürerken kapanmaz.
 */

interface ImageManagerProps {
  orderId: string;
  onClose: () => void;
}

const I = {
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  upload: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M17 8l-5-5-5 5M12 3v12" /></svg>,
  image: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m21 15-5-5L5 21" /></svg>,
  eye: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>,
  download: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M7 10l5 5 5-5M12 15V3" /></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>,
  prev: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>,
  next: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>,
};

const ImageManager: React.FC<ImageManagerProps> = ({ orderId, onClose }) => {
  const [images, setImages] = useState<OrderImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [broken, setBroken] = useState<Set<string>>(new Set()); // sunucuda dosyası olmayan resimler
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [opener] = useState(() => document.activeElement as HTMLElement | null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Alert Modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  // Confirm Modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "warning" | "danger";
    onConfirm: () => void;
  }>({ isOpen: false, message: "", type: "warning", onConfirm: () => {} });

  useEffect(() => {
    loadImages();
  }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadImages = async () => {
    try {
      setLoading(true);
      const orderImages = await orderService.getImages(orderId);
      setImages(orderImages);
    } catch (error) {
      console.error("Images could not be loaded:", error);
    } finally {
      setLoading(false);
    }
  };

  const uploadFiles = async (files: File[]) => {
    if (files.length === 0) return;

    try {
      setUploading(true);
      const result = await orderService.uploadImages(orderId, files);
      await loadImages(); // Refresh the image list
      setAlertModal({
        isOpen: true,
        message: `${result.images.length} resim başarıyla yüklendi!`,
        type: "success",
      });
    } catch (error) {
      console.error("Image upload failed:", error);
      setAlertModal({
        isOpen: true,
        message: "Resim yükleme sırasında hata oluştu!",
        type: "error",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // aynı dosya yeniden seçilebilsin
    await uploadFiles(files);
  };

  // Sürükle-bırak: dosya seçicideki accept="image/*" ile aynı süzgeç
  const handleDrop = async (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (uploading) return;
    const all = Array.from(e.dataTransfer.files || []);
    const files = all.filter((f) => f.type.startsWith("image/"));
    if (all.length > 0 && files.length === 0) {
      setAlertModal({ isOpen: true, message: "Yalnız resim dosyaları yüklenebilir.", type: "warning" });
      return;
    }
    await uploadFiles(files);
  };

  const handleDeleteImage = async (imageId: string) => {
    setConfirmModal({
      isOpen: true,
      message: "Bu resmi silmek istediğinizden emin misiniz?",
      type: "danger",
      onConfirm: async () => {
        try {
          await orderService.deleteImage(imageId);
          await loadImages(); // Refresh the image list
          setAlertModal({
            isOpen: true,
            message: "Resim başarıyla silindi!",
            type: "success",
          });
        } catch (error) {
          console.error("Image deletion failed:", error);
          setAlertModal({
            isOpen: true,
            message: "Resim silme sırasında hata oluştu!",
            type: "error",
          });
        }
      },
    });
  };

  const handleDownloadImage = async (imageUrl: string) => {
    try {
      // Image URL'den sadece path'i al
      const urlPath = imageUrl.replace(/^https?:\/\/[^/]+/, "");

      // Backend download endpoint'ini kullan (CORS sorunu yok)
      const downloadUrl = `${IMAGE_BASE_URL}/api/Order/download-image?path=${encodeURIComponent(urlPath)}`;

      // Link oluştur ve tıkla
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `image-${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Image download failed:", error);
      setAlertModal({
        isOpen: true,
        message: "Resim indirme sırasında hata oluştu!",
        type: "error",
      });
    }
  };

  // Liste değişince (silme) önizleme sınırın dışına taşmasın
  useEffect(() => {
    if (previewIndex !== null && previewIndex >= images.length) setPreviewIndex(images.length ? images.length - 1 : null);
  }, [images.length, previewIndex]);

  // Önizleme kapanınca odak, önizlemeyi açan öğeye döner
  const previewOpener = useRef<HTMLElement | null>(null);
  const openPreview = (idx: number, el: HTMLElement) => { previewOpener.current = el; setPreviewIndex(idx); };
  useEffect(() => {
    if (previewIndex === null && previewOpener.current) {
      if (previewOpener.current.isConnected) previewOpener.current.focus({ preventScroll: true });
      previewOpener.current = null;
    }
  }, [previewIndex]);

  const go = (d: number) => setPreviewIndex((i) => (i === null || images.length === 0 ? i : (i + d + images.length) % images.length));

  // Esc: onay → önizleme → pencere. Önizlemede ←/→ resimler arasında gezer.
  const keyRef = useRef<(e: KeyboardEvent) => void>(() => {});
  keyRef.current = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      if (confirmModal.isOpen) setConfirmModal((c) => ({ ...c, isOpen: false }));
      else if (previewIndex !== null) setPreviewIndex(null);
      else if (!uploading) onClose();
      return;
    }
    if (previewIndex !== null && !confirmModal.isOpen && images.length > 1 && (e.key === "ArrowLeft" || e.key === "ArrowRight")) {
      e.preventDefault();
      e.stopPropagation();
      go(e.key === "ArrowLeft" ? -1 : 1);
    }
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => keyRef.current(e);
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, []);

  useEffect(() => () => { if (opener && opener.isConnected) opener.focus?.({ preventScroll: true }); }, [opener]);
  // autoFocus yerine efekt: StrictMode geliştirmede geri-odak temizliğini bir kez erken çalıştırır, autoFocus ezilirdi
  useEffect(() => { closeRef.current?.focus({ preventScroll: true }); }, []);

  const markBroken = (id: string) => setBroken((s) => (s.has(id) ? s : new Set(s).add(id)));
  const preview = previewIndex !== null ? images[previewIndex] : undefined;
  const previewUrl = preview ? getImageUrl(preview.imageUrl) : "";

  return (
    <>
      <div className="po-overlay center top" onMouseDown={(e) => { if (e.target === e.currentTarget && !uploading) onClose(); }}>
        <div className="po-modal wide im-modal" role="dialog" aria-modal="true" aria-labelledby="im-title">
          <div className="po-modal-head">
            <div>
              <span className="po-kicker">Sipariş</span>
              <h3 id="im-title" className="im-title">
                Sipariş resimleri
                {!loading && images.length > 0 && <span className="po-badge muted">{images.length} resim</span>}
              </h3>
            </div>
            <button type="button" ref={closeRef} className="po-ib big" aria-label="Kapat" onClick={onClose} disabled={uploading}>{I.close}</button>
          </div>

          <div className="po-modal-body">
            {/* Yükleme alanı — tıkla veya sürükle-bırak */}
            <label
              htmlFor="im-image-upload"
              className={`im-drop ${dragOver ? "over" : ""} ${uploading ? "disabled" : ""}`}
              tabIndex={uploading ? -1 : 0}
              onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !uploading) { e.preventDefault(); inputRef.current?.click(); } }}
              onDragOver={(e) => { e.preventDefault(); if (!uploading) setDragOver(true); }}
              onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragOver(false); }}
              onDrop={handleDrop}
              aria-disabled={uploading}
            >
              <span className="im-drop-icon">{uploading ? <span className="po-spin" /> : I.upload}</span>
              {uploading
                ? <b>Yükleniyor…</b>
                : <><b>Resim ekle</b><span>Dosyaları buraya sürükleyin ya da seçmek için tıklayın</span></>}
              <span className="im-drop-hint">JPG, PNG, WEBP · birden fazla seçilebilir</span>
            </label>
            <input
              id="im-image-upload"
              ref={inputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileUpload}
              disabled={uploading}
              hidden
            />

            {/* Images Grid */}
            {loading ? (
              <div className="po-loading"><span className="po-spin" /> Resimler yükleniyor</div>
            ) : images.length === 0 ? (
              <div className="po-blank small">
                <span className="po-blank-icon">{I.image}</span>
                <b>Henüz resim yok</b>
                Bu siparişe ait ilk resmi yukarıdaki alandan ekleyin.
              </div>
            ) : (
              <ul className="im-grid" aria-label="Sipariş resimleri">
                {images.map((image, idx) => {
                  const imageUrl = getImageUrl(image.imageUrl);
                  const isBroken = broken.has(image.orderImageId);
                  const name = image.description || `Resim ${idx + 1}`;
                  return (
                    <li key={image.orderImageId} className="im-item">
                      <button type="button" className="im-thumb" onClick={(e) => openPreview(idx, e.currentTarget)} aria-label={`${name} önizle`}>
                        {isBroken ? (
                          <span className="im-thumb-missing">{I.image}<small>Önizleme yok</small></span>
                        ) : (
                          <img src={imageUrl} alt={name} loading="lazy" onError={() => markBroken(image.orderImageId)} />
                        )}
                      </button>
                      <div className="im-item-bar">
                        <span className="im-item-name" title={name}>{name}</span>
                        <div className="po-rowacts">
                          <button type="button" className="po-ib" title="Önizleme" aria-label={`${name} önizle`} onClick={(e) => openPreview(idx, e.currentTarget)}>{I.eye}</button>
                          <button type="button" className="po-ib" title="İndir" aria-label={`${name} indir`} onClick={() => handleDownloadImage(imageUrl)}>{I.download}</button>
                          <button type="button" className="po-ib danger" title="Sil" aria-label={`${name} sil`} onClick={() => handleDeleteImage(image.orderImageId)}>{I.trash}</button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="po-modal-foot between">
            <span className="po-hint">Resimler siparişin tüm ekranlarında görünür; silinen resim geri alınamaz.</span>
            <div className="po-foot-btns">
              <button type="button" className="po-btn" onClick={onClose} disabled={uploading}>Kapat</button>
            </div>
          </div>
        </div>
      </div>

      {/* Önizleme (lightbox) — koyu katman; dışına tıklayınca / Esc ile yalnız önizleme kapanır */}
      {preview && (
        <div className="po-overlay center top im-lightbox-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setPreviewIndex(null); }}>
          <div className="im-lightbox" role="dialog" aria-modal="true" aria-label={`${preview.description || "Resim"} önizleme`}>
            <div className="im-lightbox-stage">
              {broken.has(preview.orderImageId) ? (
                <div className="im-lightbox-msg">{I.image} Resim dosyası bulunamadı.</div>
              ) : (
                <img key={preview.orderImageId} src={previewUrl} alt={preview.description || "Büyük önizleme"} onError={() => markBroken(preview.orderImageId)} />
              )}
              {images.length > 1 && (
                <>
                  <button type="button" className="im-lightbox-nav prev" aria-label="Önceki resim" onClick={() => go(-1)}>{I.prev}</button>
                  <button type="button" className="im-lightbox-nav next" aria-label="Sonraki resim" onClick={() => go(1)}>{I.next}</button>
                </>
              )}
            </div>
            <div className="im-lightbox-bar">
              <span className="im-lightbox-cap" title={preview.description || ""}>
                <b>{preview.description || "Resim"}</b>
                {images.length > 1 && <> · {(previewIndex ?? 0) + 1}/{images.length}</>}
              </span>
              <button type="button" className="po-btn small" onClick={() => handleDownloadImage(previewUrl)}>{I.download} İndir</button>
              <button type="button" className="po-ib big" aria-label="Önizlemeyi kapat" onClick={() => setPreviewIndex(null)} autoFocus>{I.close}</button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText="Sil"
        onConfirm={() => {
          confirmModal.onConfirm();
          setConfirmModal({ ...confirmModal, isOpen: false });
        }}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
    </>
  );
};

export default ImageManager;
